import React from 'react';
import { WifiOff, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface OfflineIndicatorProps {
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  onSyncClick?: () => void;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  isOnline,
  pendingCount,
  isSyncing,
  onSyncClick,
}) => {
  const { t } = useLanguage();

  // Don't show anything if online and no pending changes
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full shadow-lg flex items-center gap-3 animate-fade-in ${
        isOnline
          ? isSyncing
            ? 'bg-blue-600/90 backdrop-blur-sm'
            : 'bg-green-600/90 backdrop-blur-sm'
          : 'bg-yellow-600/90 backdrop-blur-sm'
      }`}
    >
      {!isOnline ? (
        <>
          <WifiOff size={18} className="text-white" />
          <span className="text-white text-sm font-medium">{t('offline.notice')}</span>
          {pendingCount > 0 && (
            <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </>
      ) : isSyncing ? (
        <>
          <RefreshCw size={18} className="text-white animate-spin" />
          <span className="text-white text-sm font-medium">{t('offline.syncing')}</span>
        </>
      ) : pendingCount > 0 ? (
        <>
          <AlertCircle size={18} className="text-white" />
          <span className="text-white text-sm font-medium">{pendingCount} changes pending</span>
          <button
            onClick={onSyncClick}
            className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1 rounded-full transition-colors"
          >
            Sync Now
          </button>
        </>
      ) : (
        <>
          <Check size={18} className="text-white" />
          <span className="text-white text-sm font-medium">{t('offline.synced')}</span>
        </>
      )}
    </div>
  );
};

// Toast notification for sync status
export const SyncToast: React.FC<{
  message: string;
  type: 'success' | 'error' | 'info';
  onDismiss: () => void;
}> = ({ message, type, onDismiss }) => {
  React.useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const colors = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-blue-600',
  };

  return (
    <div
      className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${colors[type]} text-white animate-fade-in flex items-center gap-3`}
    >
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={onDismiss}
        className="text-white/70 hover:text-white transition-colors"
      >
        ×
      </button>
    </div>
  );
};
